import React, { useMemo } from 'react';
const { format } = require('date-fns');
import { startCase } from 'lodash';
import { colors } from '@/styles';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@material-ui/icons/Close';
import Popover from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import { CheckCircleOutline, HelpOutline, HighlightOffOutlined, SyncOutlined, EditOutlined } from '@material-ui/icons';
import { asBrowserDate } from '@/utilities/convertToISO';
import { formatPayPeriod, editsDueDate } from '@/utilities/payPeriod';
import { NetsuiteLink } from '@/components/NetsuiteLink';
import { BodyText } from '@/components/CarrierAccountingOrders/blocks';
import { MARKETPLACE_TIERS, calcSupplementalTotal } from '@onward-delivery/core';
import { countOrdersMissing210, hasAnySubmitted210, allSuccessful210 } from '@/utilities/orderful';

const TIER_3_PRODUCT_SERVICE_DEFAULT = 'Load Board:01-Load Board Delivery Charge (existing customers)';

const dateNumeric = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

export const useColumns = ({ callbacks, isAdmin, canViewIntegrations }) => {
    return useMemo(() => {
        return [
            {
                Header: 'Data',
                id: 'data-columns',
                columns: [
                    {
                        Header: 'Invoice',
                        id: 'invoice_number',
                        accessor: (invoice) => invoice.invoice_number || '--',
                        width: 75,
                    },
                    {
                        Header: 'Shipper',
                        id: 'business_name',
                        span: false,
                        accessor: (invoice) => invoice.client?.business_name,
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Week',
                        id: 'week_number',
                        accessor: (invoice) => invoice.week_number,
                        width: 75,
                    },
                    {
                        Header: 'Pay Period',
                        id: 'period',
                        span: false,
                        accessor: (invoice) => {
                            return formatPayPeriod(invoice.pay_period_start, invoice.pay_period_end) || '--';
                        },
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Edits Due',
                        id: 'edits_due',
                        span: false,
                        accessor: (invoice) => {
                            const thurs = editsDueDate(invoice.pay_period_end);
                            if (!thurs) return '--';

                            if (!invoice.is_pending) {
                                return `${dateNumeric.format(new Date(thurs))} (submitted)`;
                            }

                            return dateNumeric.format(thurs);
                        },
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Due',
                        id: 'due_date',
                        width: 100,
                        disableSortBy: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            return invoice.due_date ? dateNumeric.format(asBrowserDate(invoice.due_date)) : '--';
                        },
                    },
                    {
                        Header: 'POD',
                        id: 'pod',
                        width: 75,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const isMissing = !invoice.shipper_orders.every((order) => {
                                const hasPhoto = order.delivery_photo?.length >= 1 || order.pod?.length >= 1;
                                const hasSignature = order.customer_signature?.length >= 1;
                                return hasSignature || hasPhoto;
                            });

                            return !isMissing ? (
                                <>
                                    <DoneIcon
                                        css={css`
                                            color: ${colors.greens.primary};
                                        `}
                                    />
                                </>
                            ) : (
                                <CloseIcon
                                    css={css`
                                        color: ${colors.reds.primary};
                                        background: none;
                                    `}
                                />
                            );
                        },
                    },
                    {
                        Header: 'Status',
                        id: 'status',
                        width: 125,
                        span: false,
                        disableSortBy: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const label = startCase(invoice.status.toLowerCase());
                            let Icon = HelpOutline;
                            let color = 'black';
                            switch (invoice.status) {
                                case 'PAID':
                                    color = colors.greens.primary;
                                    Icon = CheckCircleOutline;
                                    break;
                                case 'APPROVED':
                                    color = colors.greens.primary;
                                    Icon = CheckCircleOutline;
                                    break;
                                case 'UNPAID':
                                case 'CANCELLED':
                                    color = colors.reds.cancelled;
                                    Icon = HighlightOffOutlined;
                                    break;
                                case 'PROCESSING':
                                    color = colors.oranges.primary;
                                    Icon = SyncOutlined;
                                    break;
                                case 'DRAFT':
                                    color = colors.greys.secondary;
                                    Icon = EditOutlined;
                                    break;
                                default:
                                    color = 'black';
                                    Icon = HelpOutline;
                                    break;
                            }

                            return (
                                <Grid
                                    container
                                    direction="row"
                                    css={css`
                                        color: ${color};
                                        height: 100%;
                                        align-items: center;
                                        flex-wrap: nowrap;
                                    `}
                                >
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 0;
                                            flex-basis: 0;
                                            margin-right: 4px;
                                        `}
                                    >
                                        <Icon />
                                    </Grid>
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 0;
                                            flex-basis: 0;
                                        `}
                                    >
                                        <BodyText>{label}</BodyText>
                                    </Grid>
                                </Grid>
                            );
                        },
                    },
                    {
                        Header: 'Subtotal',
                        id: 'subtotal',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const amount = (invoice.shipper_orders || []).reduce((acc, order) => {
                                const total =
                                    (order.shipper_rate || 0) +
                                    (order.middle_mile_shipper_rate || 0) +
                                    (order.warehouse_shipper_rate || 0);
                                return acc + total;
                            }, 0);
                            return dollarFormatter.format(amount);
                        },
                    },
                    {
                        Header: 'Claims',
                        id: 'claims',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const claimsTotal = (invoice.claim_adjustments || []).reduce((acc, { amount }) => {
                                return acc + amount;
                            }, 0);
                            return `(${dollarFormatter.format(claimsTotal)})`;
                        },
                    },
                    {
                        Header: 'Supplemental Charges',
                        id: 'supplemental_charges',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            return dollarFormatter.format(calcSupplementalTotal(invoice));
                        },
                    },
                    {
                        Header: 'Total Due',
                        id: 'revenue',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const claimsTotal = (invoice.claim_adjustments || []).reduce((acc, { amount }) => {
                                return acc + amount;
                            }, 0);
                            const ordersTotal = (invoice.shipper_orders || []).reduce((acc, order) => {
                                const total =
                                    (order.shipper_rate || 0) +
                                    (order.middle_mile_shipper_rate || 0) +
                                    (order.warehouse_shipper_rate || 0);
                                return acc + total;
                            }, 0);
                            return dollarFormatter.format(ordersTotal - claimsTotal + calcSupplementalTotal(invoice));
                        },
                    },
                    ...(canViewIntegrations
                        ? [
                              {
                                  Header: 'NetSuite',
                                  id: 'netsuite',
                                  width: 100,
                                  canSort: false,
                                  Cell: ({ row }) => <NetsuiteLink invoice={row.original} />,
                              },
                              {
                                  Header: 'Orderful',
                                  id: 'orderful',
                                  width: 100,
                                  canSort: false,
                                  Cell: ({ row }) => {
                                      const invoice = row.original;
                                      const orders = invoice.shipper_orders || [];
                                      if (!hasAnySubmitted210(invoice, orders)) {
                                          return '--';
                                      }

                                      const missing = countOrdersMissing210(invoice, orders);
                                      if (missing > 0) {
                                          return (
                                              <BodyText
                                                  css={css`
                                                      font-weight: bold;
                                                      color: ${colors.reds.primary};
                                                  `}
                                              >
                                                  {missing}
                                              </BodyText>
                                          );
                                      }

                                      return allSuccessful210(invoice, orders) ? (
                                          <DoneIcon
                                              css={css`
                                                  color: ${colors.greens.primary};
                                              `}
                                          />
                                      ) : (
                                          <CloseIcon
                                              css={css`
                                                  color: ${colors.reds.primary};
                                                  background: none;
                                              `}
                                          />
                                      );
                                  },
                              },
                          ]
                        : []),
                ],
            },
            {
                sticky: 'right',
                Header: 'Sticky',
                id: 'sticky-group',
                columns: [
                    {
                        Header: '',
                        id: 'action-1',
                        width: 45,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const [anchorEl, setAnchorEl] = React.useState(null);

                            const handleClick = (event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                setAnchorEl(event.currentTarget);
                            };

                            const handleClose = (event) => {
                                event?.stopPropagation();
                                event?.preventDefault();
                                setAnchorEl(null);
                            };

                            return (
                                <div
                                    css={css`
                                        color: #59b863;
                                        font-weight: 600;
                                        display: flex-start;
                                        justify-content: flex-start;
                                        align-items: center;
                                    `}
                                >
                                    <IconButton
                                        disabled={invoice.status === 'PAID'}
                                        onClick={handleClick}
                                        css={css`
                                            font-weight: 600;
                                            padding: 4px;
                                            justify-content: center;
                                        `}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Popover
                                        open={Boolean(anchorEl)}
                                        anchorEl={anchorEl}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'center',
                                        }}
                                        style={{ padding: '10px', borderRadius: '20px' }}
                                    >
                                        <Grid direction="column" container>
                                            {isAdmin && ['DRAFT'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.submitId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Submit
                                                    </Button>
                                                </Grid>
                                            )}
                                            {isAdmin && ['UNPAID'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.approveId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Approve
                                                    </Button>
                                                </Grid>
                                            )}
                                            {isAdmin && ['UNPAID', 'APPROVED'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.payId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Mark as Paid
                                                    </Button>
                                                </Grid>
                                            )}
                                            {isAdmin && !['APPROVED', 'PAID'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.cancelId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Popover>
                                </div>
                            );
                        },
                        span: false,
                    },
                ],
            },
        ];
    }, [callbacks, isAdmin, canViewIntegrations]);
};

export const QUICKBOOKS_CSV_COLUMNS = [
    {
        header: 'Invoice Number',
        getValue: ({ invoice }) => invoice.invoice_number,
    },
    {
        header: 'Customer',
        getValue: ({ invoice }) => invoice.client.business_name,
    },
    {
        header: 'Email',
        getValue: ({ invoice }) => invoice.client?.billing_email || invoice.client?.email || '',
    },
    {
        header: 'Invoice Date',
        getValue: ({ invoice }) => {
            return invoice.created_at ? format(new Date(invoice.created_at), 'MM/dd/yyyy') : '';
        },
    },
    {
        header: 'Due Date',
        getValue: ({ invoice }) => {
            return invoice.due_date ? format(new Date(invoice.due_date), 'MM/dd/yyyy') : '';
        },
    },
    {
        header: 'Date Completed',
        getValue: ({ order }) => {
            return order.billing_completion_time ? format(new Date(order.billing_completion_time), 'MM/dd/yyyy') : '';
        },
    },
    {
        header: 'Reference Number',
        getValue: ({ order }) => order.order_number,
    },
    {
        header: 'Purchase Order',
        getValue: ({ order }) => [order.po_number, order.reference_id].filter(Boolean).join(' / '),
    },
    {
        header: 'Product/Service',
        getValue: ({ row, order }) => {
            if (row?.qbo_tag) {
                return row.qbo_tag;
            }
            const isTier3 = order?.shipper_rate_override?.marketplace_tier === MARKETPLACE_TIERS.T3_FULLY_MANAGED;
            return isTier3 ? TIER_3_PRODUCT_SERVICE_DEFAULT : '';
        },
    },
    {
        header: 'Description',
        getValue: ({ row }) => {
            return row?.description || '';
        },
    },
    {
        header: 'Quantity',
        getValue: ({ row }) => {
            return row?.quantity || '';
        },
    },
    {
        header: 'Rate',
        getValue: ({ row }) => {
            return dollarFormatter.format(row?.rate || 0);
        },
    },
    {
        header: 'Amount',
        getValue: ({ row }) => {
            return dollarFormatter.format(row?.total || 0);
        },
    },
    {
        header: 'Class',
        getValue: ({ order }) => {
            return order?.invoice_class || '';
        },
    },
    {
        header: 'Memo',
        getValue: ({ order }) => {
            return [order.order_number, order.po_number, order.reference_id].filter(Boolean).join(' / ');
        },
    },
];
